import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, CalendarDays, CheckCircle, ExternalLink, Eye, Search, Clock, User, XCircle } from 'lucide-react'
import { workersApi } from '@/services/workers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { formatDateForLocale, useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import type { Worker } from '@/types'
import type { WorkerLeaveRequest, WorkerLeaveStatus } from '@/services/workers'

const getWorkerFullName = (worker: Worker) =>
  worker.display_name || worker.full_name || worker.user_registration_number || `Worker ${worker.id}`

type LeaveScheduleConflict = {
  id?: string | number
  entry_id?: string | number
  workday_entry_id?: string | number
  job_title?: string | null
  job_ref?: string | null
  work_date?: string | null
  planned_start?: string | null
  planned_end?: string | null
  worker_name?: string | null
}

export default function WorkerApprovalsPage() {
  const serviceInstanceId = getServiceInstanceIdFromSearch(window.location.search)
  const {
    locale: organizationDateLocale,
    formatTime: formatOrganizationTime,
  } = useOrganizationDateTimeFormat(serviceInstanceId)
  const [search, setSearch] = useState('')
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)
  const [leaveStatus, setLeaveStatus] = useState<WorkerLeaveStatus>('pending')
  const [pendingLeaveApprovalId, setPendingLeaveApprovalId] = useState<string | number | null>(null)
  const [leaveConflictDialog, setLeaveConflictDialog] = useState<{
    message: string
    conflicts: LeaveScheduleConflict[]
  } | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-approvals', search],
    queryFn: () => workersApi.getPendingApprovals({ page_size: 50 }),
  })

  const leaveRequestsQuery = useQuery({
    queryKey: ['admin-leave-requests', leaveStatus],
    queryFn: () => workersApi.getAdminLeaveRequests({ status: leaveStatus }),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => workersApi.approveWorker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] })
      queryClient.invalidateQueries({ queryKey: ['workers'] })
      toast.success('Worker approved successfully')
      setSelectedWorker(null)
      setActionType(null)
    },
    onError: () => {
      toast.error('Failed to approve worker')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => workersApi.rejectWorker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] })
      queryClient.invalidateQueries({ queryKey: ['workers'] })
      toast.success('Worker rejected')
      setSelectedWorker(null)
      setActionType(null)
    },
    onError: () => {
      toast.error('Failed to reject worker')
    },
  })

  const approveLeaveMutation = useMutation({
    mutationFn: ({ id, force }: { id: string | number; force?: boolean }) => (
      workersApi.approveAdminLeaveRequest(id, { allow_schedule_clash: Boolean(force) })
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-leave-requests'] })
      toast.success('Leave request approved')
      setLeaveConflictDialog(null)
      setPendingLeaveApprovalId(null)
    },
    onError: (error, variables) => {
      const conflictPayload = getLeaveConflictPayload(error)
      if (conflictPayload.isScheduleConflict || conflictPayload.conflicts.length > 0) {
        setPendingLeaveApprovalId(variables.id)
        setLeaveConflictDialog(conflictPayload)
        return
      }
      setPendingLeaveApprovalId(null)
      toast.error(conflictPayload.message || (error instanceof Error ? error.message : 'Failed to approve leave request'))
    },
  })

  const rejectLeaveMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string | number; reason?: string }) =>
      workersApi.rejectAdminLeaveRequest(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-leave-requests'] })
      toast.success('Leave request rejected')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to reject leave request')
    },
  })

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const handleAction = () => {
    if (!selectedWorker) return
    if (actionType === 'approve') {
      approveMutation.mutate(selectedWorker.id)
    } else if (actionType === 'reject') {
      rejectMutation.mutate(selectedWorker.id)
    }
  }

  const handleRejectLeave = (request: WorkerLeaveRequest) => {
    const reason = window.prompt('Enter rejection reason')
    if (reason === null) return
    rejectLeaveMutation.mutate({ id: request.id, reason: reason.trim() || undefined })
  }

  const getLeaveStatusBadgeClass = (status: WorkerLeaveStatus) => {
    if (status === 'approved') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    if (status === 'rejected') return 'bg-rose-50 text-rose-700 border-rose-200'
    if (status === 'cancelled') return 'bg-slate-50 text-slate-700 border-slate-200'
    return 'bg-amber-50 text-amber-700 border-amber-200'
  }

  const conflictEntryId = (entry: LeaveScheduleConflict) =>
    String(entry.entry_id ?? entry.workday_entry_id ?? entry.id ?? '').trim()

  const conflictKey = (entry: LeaveScheduleConflict) => {
    const entryId = conflictEntryId(entry)
    if (entryId) return `entry:${entryId}`
    return [
      entry.job_title,
      entry.job_ref,
      entry.work_date,
      entry.planned_start,
      entry.planned_end,
      entry.worker_name,
    ].map((part) => String(part ?? '').trim()).join('|')
  }

  const dedupeConflicts = (conflicts: LeaveScheduleConflict[]) => {
    const seen = new Set<string>()
    return conflicts.filter((entry) => {
      const key = conflictKey(entry)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

  const parseJsonString = (value: string): unknown => {
    const trimmed = value.trim()
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
    try {
      return JSON.parse(trimmed)
    } catch {
      return null
    }
  }

  const findConflictList = (value: unknown, visited = new Set<unknown>()): LeaveScheduleConflict[] => {
    if (value == null) return []
    if (typeof value === 'string') {
      const parsed = parseJsonString(value)
      return parsed == null ? [] : findConflictList(parsed, visited)
    }
    if (Array.isArray(value)) {
      return value.filter(isRecord) as LeaveScheduleConflict[]
    }
    if (!isRecord(value) || visited.has(value)) return []
    visited.add(value)

    for (const key of ['conflicts', 'schedule_conflicts', 'scheduleConflicts', 'entries', 'schedule_entries']) {
      const raw = value[key]
      if (Array.isArray(raw)) return raw.filter(isRecord) as LeaveScheduleConflict[]
      const nested = findConflictList(raw, visited)
      if (nested.length > 0) return nested
    }

    for (const key of ['data', 'more_info', 'error', 'detail', 'response', 'body']) {
      const nested = findConflictList(value[key], visited)
      if (nested.length > 0) return nested
    }

    return []
  }

  const findMessage = (value: unknown, visited = new Set<unknown>()): string | null => {
    if (typeof value === 'string') {
      const parsed = parseJsonString(value)
      if (parsed != null) return findMessage(parsed, visited) || value
      return value.trim() || null
    }
    if (!isRecord(value) || visited.has(value)) return null
    visited.add(value)

    for (const key of ['message', 'detail', 'error']) {
      const raw = value[key]
      if (typeof raw === 'string' && raw.trim()) return raw.trim()
      const nested = findMessage(raw, visited)
      if (nested) return nested
    }

    for (const key of ['data', 'more_info', 'response', 'body']) {
      const nested = findMessage(value[key], visited)
      if (nested) return nested
    }

    return null
  }

  const isScheduleConflictMessage = (message: string) => {
    const normalized = message.toLowerCase()
    return normalized.includes('already scheduled during this leave period')
      || normalized.includes('schedule entries found')
  }

  const getLeaveConflictPayload = (error: unknown): {
    message: string
    conflicts: LeaveScheduleConflict[]
    isScheduleConflict: boolean
  } => {
    const responseData = (error as { response?: { data?: unknown } })?.response?.data
    const candidates = [
      responseData,
      (error as { data?: unknown })?.data,
      (error as { response?: unknown })?.response,
      error,
    ]
    const conflicts = dedupeConflicts(candidates.flatMap((candidate) => findConflictList(candidate)))
    const message = candidates
      .map((candidate) => findMessage(candidate))
      .find((candidate): candidate is string => Boolean(candidate))
      || (error instanceof Error ? error.message : '')
      || 'Worker is already scheduled during this leave period.'
    return {
      message,
      conflicts,
      isScheduleConflict: isScheduleConflictMessage(message),
    }
  }

  const formatConflictDateTime = (value?: string | null) => {
    if (!value) return null
    return formatOrganizationTime(value)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load pending approvals</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  const filteredWorkers = data?.results?.filter(worker =>
    getWorkerFullName(worker).toLowerCase().includes(search.toLowerCase()) ||
    (worker.email || '').toLowerCase().includes(search.toLowerCase())
  ) || []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Pending Approvals</h1>
        <p className="text-muted-foreground">Review worker registrations and leave requests</p>
      </div>

      <Tabs defaultValue="workers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="workers">Worker registrations</TabsTrigger>
          <TabsTrigger value="leave">Leave requests</TabsTrigger>
        </TabsList>

        <TabsContent value="workers">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-lg font-medium">Workers Awaiting Approval</CardTitle>
                  <CardDescription>
                    {data?.count || 0} worker(s) pending review
                  </CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search workers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 w-full sm:w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 rounded-lg border">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-48 mb-2" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredWorkers.length > 0 ? (
                <div className="space-y-4">
                  {filteredWorkers.map((worker) => (
                <div
                  key={worker.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={worker.avatar_url || worker.avatar} alt={getWorkerFullName(worker)} />
                      <AvatarFallback className="bg-amber-100 text-amber-700">
                        {getInitials(getWorkerFullName(worker))}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <Link 
                          to={withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId)}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {getWorkerFullName(worker)}
                        </Link>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <Clock className="mr-1 h-3 w-3" />
                          {worker.registration_status_label || 'Pending'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{worker.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Applied: {formatDateForLocale(worker.created_at, organizationDateLocale, 'Unknown')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId)}>
                        <Eye className="mr-2 h-4 w-4" />
                        Review
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => {
                        setSelectedWorker(worker)
                        setActionType('approve')
                      }}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive border-destructive/20 hover:bg-destructive/10"
                      onClick={() => {
                        setSelectedWorker(worker)
                        setActionType('reject')
                      }}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Reject
                    </Button>
                  </div>
                </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">All caught up!</p>
                  <p>No workers pending approval at this time.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-lg font-medium">Leave Requests</CardTitle>
                  <CardDescription>Approve or reject worker leave requests.</CardDescription>
                </div>
                <div className="flex gap-2">
                  {(['pending', 'approved', 'rejected'] as WorkerLeaveStatus[]).map((status) => (
                    <Button
                      key={status}
                      variant={leaveStatus === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLeaveStatus(status)}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {leaveRequestsQuery.isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-20 w-full rounded-lg" />
                  ))}
                </div>
              ) : (leaveRequestsQuery.data ?? []).length > 0 ? (
                <div className="space-y-4">
                  {(leaveRequestsQuery.data ?? []).map((request) => (
                    <div key={String(request.id)} className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{request.worker_name || `Worker ${request.worker}`}</p>
                          <Badge variant="outline" className={getLeaveStatusBadgeClass(request.status)}>
                            {request.status}
                          </Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {request.leave_type_name || 'Leave'} · {formatDateForLocale(request.start_date, organizationDateLocale)} - {formatDateForLocale(request.end_date, organizationDateLocale)}
                          {request.day_count ? ` · ${request.day_count} day${request.day_count === 1 ? '' : 's'}` : ''}
                        </p>
                        {request.reason ? <p className="mt-1 text-sm text-muted-foreground">{request.reason}</p> : null}
                      </div>
                      {request.status === 'pending' ? (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleRejectLeave(request)} disabled={rejectLeaveMutation.isPending}>
                            <XCircle className="mr-2 h-4 w-4" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setPendingLeaveApprovalId(request.id)
                              approveLeaveMutation.mutate({ id: request.id })
                            }}
                            disabled={approveLeaveMutation.isPending}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Approve
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No {leaveStatus} leave requests</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!selectedWorker && !!actionType} onOpenChange={() => {
        setSelectedWorker(null)
        setActionType(null)
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionType === 'approve' ? 'Approve Worker' : 'Reject Worker'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === 'approve'
                ? `Are you sure you want to approve ${selectedWorker ? getWorkerFullName(selectedWorker) : 'this worker'}? They will gain access to the worker portal.`
                : `Are you sure you want to reject ${selectedWorker ? getWorkerFullName(selectedWorker) : 'this worker'}? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={actionType === 'reject' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!leaveConflictDialog} onOpenChange={() => {
        setLeaveConflictDialog(null)
        setPendingLeaveApprovalId(null)
      }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-amber-100 p-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <AlertDialogTitle>Schedule conflicts</AlertDialogTitle>
                <AlertDialogDescription className="mt-1">
                  {leaveConflictDialog?.message}
                </AlertDialogDescription>
              </div>
            </div>
            <AlertDialogDescription className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              Click a scheduled entry to review it before approving this leave request.
            </AlertDialogDescription>
            <AlertDialogDescription className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
              Force approve will withdraw overlapping schedules and set reason to On Leave.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-96 space-y-3 overflow-y-auto">
            {(leaveConflictDialog?.conflicts ?? []).length === 0 ? (
              <div className="rounded-xl border bg-card p-4 text-sm text-muted-foreground">
                Schedule details were not included in the server response. Refresh and try again to view the linked entries.
              </div>
            ) : (leaveConflictDialog?.conflicts ?? []).map((entry) => {
              const entryId = conflictEntryId(entry)
              const jobTitle = entry.job_title || entry.job_ref || 'Scheduled job'
              const workDate = entry.work_date
                ? formatDateForLocale(entry.work_date, organizationDateLocale)
                : 'Date unavailable'
              const plannedStart = formatConflictDateTime(entry.planned_start)
              const plannedEnd = formatConflictDateTime(entry.planned_end)
              const content = (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-2">
                      <p className="truncate text-sm font-semibold text-foreground">{jobTitle}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-muted-foreground">
                          <CalendarDays className="h-3 w-3" />
                          {workDate}
                        </span>
                        {(plannedStart || plannedEnd) ? (
                          <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {plannedStart || '?'} - {plannedEnd || '?'}
                          </span>
                        ) : null}
                        {entry.worker_name ? (
                          <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-muted-foreground">
                            <User className="h-3 w-3" />
                            {entry.worker_name}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {entryId ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
                        Open entry
                        <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    ) : null}
                  </div>
              )
              if (!entryId) {
                return (
                  <div
                    key={`${jobTitle}-${workDate}`}
                    className="block rounded-xl border bg-card p-4 shadow-sm"
                  >
                    {content}
                  </div>
                )
              }
              return (
                <Link
                  key={`${entryId}-${workDate}`}
                  to={withServiceInstanceId(`/workdays/${entryId}/edit`, serviceInstanceId)}
                  className="group block rounded-xl border bg-card p-4 shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/40"
                  onClick={() => setLeaveConflictDialog(null)}
                >
                  {content}
                </Link>
              )
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction
              disabled={approveLeaveMutation.isPending || !pendingLeaveApprovalId}
              onClick={() => {
                if (!pendingLeaveApprovalId) {
                  return
                }
                approveLeaveMutation.mutate({ id: pendingLeaveApprovalId, force: true })
              }}
            >
              Force approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
